import ProgressBar from "../athlete-progress-bar";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faCirclePlay,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { useState } from "react";
import { useAxiosInstance } from "../../../services/Interceptor";
import { getMeasurementHistory } from "../../../services/AthleteService";
import MeasurementModal from "./measurement-modal";
import VideoPlayer from "../VideoPlayer";
import { TooltipComments } from "./tooltip-comments";
import { toast } from "react-toastify";
import PremiumFeatureModal from "../../PremiumFeatureModal";
import FreeTrialModal from "../../FreeTrialModal";
import { StartFreeTrial } from "../../../services/PaymentService";
import { usePremiumUpsellRedirect } from "../../../hooks/usePremiumUpsellRedirect";
import { setProfileAction } from "../../../store/AthleteActions";
import { validateSubscriptionAction } from "../../../store/userActions";
import { bumpMenuRefresh } from "../../../store/slices/athleteSlice";
import { useSubscriptionAccess } from "../../../hooks/useSubscriptionAccess";

export default function ShowMeasurements({
  title,
  value,
  score,
  videoUrls = [],
  userId,
  heightComment = "",
  isWeightMeasurement = false,
}) {
  const dispatch = useDispatch();
  const { user, hasSubscriptionAccess, showFreeTrial } =
    useSubscriptionAccess();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isGraphOpen, setIsGraphOpen] = useState(false);
  const [measurementData, setMeasurementData] = useState([]);
  const [openPremiumFeatureModal, setOpenPremiumFeatureModal] = useState(false);
  const [openTrialModal, setOpenTrialModal] = useState(false);

  const apiInstance = useAxiosInstance();
  const numericScore = Number(score);
  const roundedScore = Number.isFinite(numericScore)
    ? Math.round(numericScore)
    : null;

  const openSubscription = usePremiumUpsellRedirect(user);

  const handlePremiumFreeTrial = () => {
    setOpenPremiumFeatureModal(false);
    setOpenTrialModal(true);
  };

  const handlePremiumSubscribe = () => {
    setOpenPremiumFeatureModal(false);
    openSubscription();
  };

  const openModalFN = () => {
    if (!hasSubscriptionAccess) {
      setOpenPremiumFeatureModal(true);
      return;
    }
    setSelectedVideos(videoUrls);
    setIsOpen(true);
  };

  const closeModalFN = () => {
    setSelectedVideos([]);
    setIsOpen(false);
  };

  const openGraphModal = async () => {
    if (!hasSubscriptionAccess) {
      setOpenPremiumFeatureModal(true);
      return;
    }
    try {
      const data = await getMeasurementHistory(apiInstance, userId, title);
      setMeasurementData(data);
      setIsGraphOpen(true);
    } catch (error) {
      console.error("No se pudo cargar el gráfico de medidas", error);
    }
  };

  const closeGraphModal = () => {
    setIsGraphOpen(false);
    setMeasurementData([]);
  };

  const handleInfoClick = () => {
    if (!hasSubscriptionAccess) {
      setOpenPremiumFeatureModal(true);
    }
  };

  const handleDownload = async () => {
    if (!videoUrls[0]) return;
    setIsDownloading(true);
    try {
      const blob = await (await fetch(videoUrls[0])).blob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "video.mp4";
      a.click();

      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error("Download error:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <PremiumFeatureModal
        open={openPremiumFeatureModal}
        onClose={() => setOpenPremiumFeatureModal(false)}
        title="Premium Feature"
        description="This feature is locked. Start a free trial or subscribe now to access it."
        onStartFreeTrial={handlePremiumFreeTrial}
        onSubscribe={handlePremiumSubscribe}
        showStartFreeTrial={showFreeTrial}
      />
      <FreeTrialModal
        open={openTrialModal}
        onClose={() => setOpenTrialModal(false)}
        user={user}
        onStoreSuccessChain={async () => {
          const result = await StartFreeTrial(apiInstance, user.id);
          if (result.success) {
            toast.success("Free trial started!", { position: "top-right" });
            setOpenTrialModal(false);
            await dispatch(setProfileAction(apiInstance, user.id));
            await dispatch(
              validateSubscriptionAction(apiInstance, user.role, user.id),
            );
            dispatch(bumpMenuRefresh());
          } else {
            toast.error("Failed to start free trial", {
              position: "top-right",
            });
          }
        }}
      />
      <div className="flex flex-col gap-1 xl:gap-5 sm:flex-row sm:items-center bg-white shadow rounded-lg border border-gray-200 py-2 px-3 w-full mb-2">
        {/* Weight + Value + Number in one line (75% on desktop) */}
        <div className="flex items-center justify-between w-full sm:w-3/4 space-x-8">
          <div className="flex flex-col w-full">
            <div className="font-normal text-gray-600">
              {title}:{" "}
              <span className="pl-4 font-medium text-gray-900">{value}</span>
            </div>
            {/* Progress bar — visible to all users */}
            <div>
              {roundedScore !== null && roundedScore >= 0 && (
                <div className="w-full ProgressBar-size">
                  <ProgressBar
                    score={roundedScore}
                    barColor={isWeightMeasurement ? "#1E4ED8" : undefined}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Icons Section (25% on desktop) */}
        <div className="flex items-center justify-start lg:justify-end xl:justify-end  sm:w-1/4 space-x-4 sm:mt-0">
          <button className=" text-white grid ">
            <FontAwesomeIcon
              icon={faCirclePlay}
              className={`text-xl ${
                videoUrls.length > 0
                  ? "text-black font-bold cursor-pointer pl-2"
                  : "text-gray-400 opacity-50 cursor-auto pl-2"
              }`}
              onClick={videoUrls.length > 0 ? openModalFN : undefined}
            />
            <span className="text-[12px] text-gray-600 font-normal">
              {" "}
              Video
            </span>
          </button>
          <button className="text-blue-500">
            <img
              src="/chart (1).svg"
              alt="graph"
              className="cursor-pointer hover:opacity-80 h-5 pl-2 mt-1"
              onClick={openGraphModal}
            />
            <span className="text-[12px] text-gray-600 font-normal">
              {" "}
              Trends
            </span>
          </button>
          {title === "Height" && heightComment && (
            <>
              <div className="relative group grid" onClick={handleInfoClick}>
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className="h-5 cursor-pointer text-gray-800"
                />
                <span className="text-[12px] text-gray-600 font-normal">
                  Info
                </span>
                {hasSubscriptionAccess && (
                  <TooltipComments result={heightComment} />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="absolute inset-0 bg-gray-900 opacity-50"></div>

          <div className="bg-white rounded-lg shadow-lg z-50 w-full   max-w-5xl min-h-[500px] p-4 m-2 overflow-y-auto">
            <div className="flex justify-between items-center mb-1">
              <div className="text-lg font-bold flex">
                {title}
                <div className="pl-16 font-normal">
                  {videoUrls.length > 0 && (
                    <button
                      onClick={handleDownload}
                      disabled={isDownloading}
                      className={`ml-2 px-3 py-1 rounded text-sm flex items-center gap-2 ${
                        isDownloading
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-600 hover:text-black"
                      } bg-transparent`}
                    >
                      {isDownloading ? "Downloading..." : "Download"}
                      <FontAwesomeIcon
                        icon={faDownload}
                        className="text-gray-500"
                      />
                    </button>
                  )}
                </div>
              </div>
              <button
                className="text-gray-600 hover:text-black text-xl font-bold safari-text"
                onClick={closeModalFN}
              >
                ✕
              </button>
            </div>
            <div className="relative w-full flex gap-2 flex-wrap justify-center pt-2">
              {selectedVideos.map((video, index) => (
                <VideoPlayer
                  key={index}
                  src={video}
                  width="100%"
                  height="500px"
                  overlay={false}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal para gráficos */}
      <MeasurementModal
        isOpen={isGraphOpen}
        closeModal={closeGraphModal}
        measurementData={measurementData}
        title={title}
      />
    </>
  );
}
